import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowLeft, CheckCircle, Package } from 'lucide-react';

const productsData = {
  'standard-pallet-racking': {
    name: 'Pallet Racking Rental Solutions',
    tagline: 'Heavy-Duty Storage on Flexible Rental Models',
    seoTitle: 'Pallet Racking Rental Solutions in India & Middle East',
    description: 'Warehaus+ provides heavy-duty pallet racking systems on flexible rental models designed for fast-growing warehouses and 3PL operators. Our modular racking structures are engineered for rapid deployment and high load performance.',
    image: 'https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/si0fgnpz_IMG_0309.png',
    features: [
      'High load capacity beams',
      'VNA compatible layouts',
      'Seismic compliant configurations',
      'Adjustable beam levels',
      'Quick installation cycles',
      'Modular design for expansion'
    ],
    applications: ['Distribution hubs', 'Seasonal inventory expansion', 'Project-based warehousing', 'Overflow storage', '3PL operations'],
    benefits: [
      { title: 'Flexible Rental Model', description: 'No heavy CAPEX required - scale your storage on operational terms.' },
      { title: 'Rapid Deployment', description: '6-8 weeks installation cycle for immediate capacity expansion.' },
      { title: 'High Load Performance', description: 'Engineered for heavy-duty applications with seismic compliance.' },
    ],
    specs: [
      { label: 'Load Capacity', value: 'Up to 3,000 kg per beam level' },
      { label: 'Frame Heights', value: '3m to 12m' },
      { label: 'Beam Lengths', value: '1.8m to 3.6m' },
      { label: 'Installation', value: '6-8 weeks typical' },
    ],
    cta: 'Request Layout Design'
  },
  'adjustable-pallet-racking': {
    name: 'Adjustable Pallet Racking',
    tagline: 'Flexibility Meets Performance',
    seoTitle: 'Adjustable Pallet Racking Systems for Rent',
    description: 'Flexible racking system with adjustable beam heights to accommodate varying product dimensions. Perfect for dynamic inventory environments with changing storage needs.',
    image: 'https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/gda04au8_IMG_0310.png',
    features: [
      'Variable beam heights at 50mm increments',
      'Quick tool-free reconfiguration',
      'High load capacity design',
      'Modular and expandable',
      'Compatible with WMS integration',
      'Safety clips and locking mechanisms'
    ],
    applications: ['Dynamic inventory facilities', 'Seasonal storage', 'Multi-product warehouses', '3PL operations', 'E-commerce fulfillment'],
    benefits: [
      { title: 'Adaptability', description: 'Quickly adjust to changing inventory profiles without structural modifications.' },
      { title: 'Space Optimization', description: 'Minimize wasted vertical space by matching beam heights to product dimensions.' },
      { title: 'Rental Flexibility', description: 'Scale up or down based on business cycles without capital lock-in.' },
    ],
    specs: [
      { label: 'Load Capacity', value: 'Up to 4,500 kg per beam level' },
      { label: 'Adjustment Pitch', value: '50mm increments' },
      { label: 'Frame Depths', value: '800mm to 1,200mm' },
      { label: 'Installation', value: '6-8 weeks typical' },
    ],
    cta: 'Request Quote'
  },
  'drive-in-racking': {
    name: 'Drive-In Pallet Racking',
    tagline: 'Maximum Density, Minimum Footprint',
    seoTitle: 'Drive-In Racking Systems for High-Density Storage',
    description: 'High-density storage solution ideal for bulk storage of similar products with LIFO inventory management. Perfect for cold storage and high-volume operations requiring maximum space utilization.',
    image: 'https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/yi6oc4bi_IMG_0311.png',
    features: [
      'Up to 85% floor space utilization',
      'LIFO inventory management',
      'Minimal aisle requirements',
      'Ideal for homogeneous products',
      'Cold storage compatible',
      'Heavy-duty construction'
    ],
    applications: ['Cold storage facilities', 'Bulk warehousing', 'Food & beverage', 'Manufacturing buffer storage', 'Seasonal inventory'],
    benefits: [
      { title: 'Space Efficiency', description: 'Store more pallets in the same footprint by eliminating multiple aisles.' },
      { title: 'Cost Savings', description: 'Reduce real estate costs per pallet stored with rental flexibility.' },
      { title: 'Ideal for Bulk', description: 'Perfect for large quantities of similar products.' },
    ],
    specs: [
      { label: 'Space Utilization', value: 'Up to 85%' },
      { label: 'Lane Depth', value: 'Up to 10 pallets deep' },
      { label: 'Height', value: 'Up to 10m' },
      { label: 'Temperature Range', value: '-30°C to +40°C' },
    ],
    cta: 'Request Quote'
  },
  'shuttle-racking': {
    name: 'High-Density Pallet Shuttle Systems',
    tagline: 'Flexible Rental for Maximum Density',
    seoTitle: 'High-Density Pallet Shuttle Systems on Flexible Rental',
    description: 'Increase storage density without permanent infrastructure commitment. Warehaus+ deploys rail-guided shuttle systems suitable for deep-lane storage and high SKU consolidation.',
    image: 'https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/z96dsxp8_IMG_0312.png',
    features: [
      'Reduced aisle requirement',
      'Higher pallet density',
      'Optimized warehouse footprint',
      'Scalable deployment',
      'FIFO or LIFO flexibility',
      'Remote monitoring capability'
    ],
    applications: ['High-volume operations', 'Buffer storage', 'Automated warehouses', 'Cold chain logistics', 'Beverage industry'],
    benefits: [
      { title: 'No Permanent Commitment', description: 'Deploy high-density storage without permanent infrastructure investment.' },
      { title: 'Increased Throughput', description: 'Shuttle systems significantly speed up storage and retrieval operations.' },
      { title: 'Scalable Deployment', description: 'Start small and scale up based on operational requirements.' },
    ],
    specs: [
      { label: 'Shuttle Speed', value: 'Up to 1.0 m/s' },
      { label: 'Lane Depth', value: 'Up to 40 pallets' },
      { label: 'Lifting Capacity', value: '1,500 kg' },
      { label: 'Battery Life', value: '8-12 hours operation' },
    ],
    cta: 'Request Configuration'
  },
  'asrs-racking': {
    name: 'ASRS Based Racking',
    tagline: 'Full Automation, Maximum Precision',
    seoTitle: 'Automated Storage & Retrieval Systems (ASRS)',
    description: 'Fully automated storage and retrieval systems for pallets, totes, and cartons. The ultimate solution for high-throughput, high-accuracy operations requiring 24/7 capability.',
    image: 'https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/z96dsxp8_IMG_0312.png',
    features: [
      'Full automation capability',
      'Minimal labor requirements',
      '99.9%+ picking accuracy',
      '24/7 operation capability',
      'WMS/ERP integration',
      'Real-time inventory tracking'
    ],
    applications: ['High-volume fulfillment', 'Pharmaceutical storage', 'Automotive parts', 'Electronics distribution', 'High-value goods'],
    benefits: [
      { title: 'Maximum Accuracy', description: 'Automated systems virtually eliminate picking errors.' },
      { title: 'Labor Optimization', description: 'Reduce dependency on manual labor for storage operations.' },
      { title: 'Continuous Operation', description: '24/7 operation capability for maximum throughput.' },
    ],
    specs: [
      { label: 'Picking Accuracy', value: '99.9%+' },
      { label: 'Operation Mode', value: '24/7 capable' },
      { label: 'Height Range', value: 'Up to 40m' },
      { label: 'Throughput', value: 'Up to 500 pallets/hour' },
    ],
    cta: 'Request Consultation'
  },
  'mezzanine-floors': {
    name: 'Multi-Tier & Mezzanine Storage Systems',
    tagline: 'Maximize Vertical Cube Utilization',
    seoTitle: 'Multi-Tier & Mezzanine Storage Systems for Rent',
    description: 'Maximize vertical cube utilization with G+2 to G+4 modular multi-tier shelving systems available on rental. Ideal for e-commerce fulfillment and spare parts storage.',
    image: 'https://customer-assets.emergentagent.com/job_a58fca7f-e2d8-46ca-adc2-538617ed4471/artifacts/tqm4hqmx_IMG_0313.png',
    features: [
      'Expand usable storage area without civil modification',
      'Bolt-based modular assembly',
      'Engineered load compliance',
      'Fast dismantling capability',
      'G+2 to G+4 configurations',
      'Integrated staircases and lifts'
    ],
    applications: ['E-commerce fulfillment', 'Spare parts storage', 'Light manufacturing', 'Retail backroom', 'Document archival'],
    benefits: [
      { title: 'Double Your Space', description: 'Expand usable storage area without civil modification or building expansion.' },
      { title: 'Modular Assembly', description: 'Bolt-based design allows quick installation and future dismantling.' },
      { title: 'Rental Flexibility', description: 'Access multi-tier storage capability without capital commitment.' },
    ],
    specs: [
      { label: 'Configurations', value: 'G+2 to G+4 levels' },
      { label: 'Load Capacity', value: 'Up to 500 kg/sqm per level' },
      { label: 'Assembly', value: 'Bolt-based modular' },
      { label: 'Installation', value: '6-8 weeks typical' },
    ],
    cta: 'Request Layout Design'
  },
};

export default function ProductDetail() {
  const { productId } = useParams();
  const product = productsData[productId];

  if (!product) {
    return (
      <div className="bg-[#0a1628] pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-white mb-4">Product Not Found</h1>
          <Link to="/products">
            <Button className="btn-primary">Back to Products</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0a1628] pt-20" data-testid="product-detail-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="container-custom relative z-10">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm mb-8">
            <Link to="/products" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Products
            </Link>
            <span className="text-gray-600">/</span>
            <span className="text-[#E63946]">{product.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
                {product.tagline}
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase leading-[0.95] tracking-tighter mb-6">
                {product.name}
              </h1>
              <p className="text-gray-400 text-lg leading-relaxed mb-8">
                {product.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/get-quote" data-testid="product-get-quote">
                  <Button className="btn-gold clip-slant h-12 px-8">
                    Get a Quote <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button className="btn-secondary h-12 px-8">
                    Contact Sales
                  </Button>
                </Link>
              </div>
            </div>

            <div className="industrial-card bg-[#0f1e32] border border-white/10 p-2">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-[#0f1e32]" data-testid="product-features">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
                Features
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-8">
                Key Features
              </h2>
              <div className="space-y-4">
                {product.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 bg-[#0a1628] border border-white/5 hover:border-[#E63946]/30 transition-colors"
                  >
                    <CheckCircle className="w-5 h-5 text-[#E63946] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
                Applications
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-8">
                Ideal For
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {product.applications.map((app, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-4 bg-[#0a1628] border border-white/5"
                  >
                    <Package className="w-5 h-5 text-[#E63946]" />
                    <span className="text-gray-300 text-sm">{app}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24" data-testid="product-benefits">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
              Benefits
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
              Why Choose This Solution
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {product.benefits.map((benefit, index) => (
              <div
                key={index}
                className="industrial-card bg-[#0f1e32] border border-white/10 p-8 hover:border-[#E63946]/50 transition-colors"
              >
                <div className="font-mono text-2xl text-[#E63946] mb-4">0{index + 1}</div>
                <h3 className="font-heading text-xl font-bold text-white uppercase mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Specifications Section */}
      <section className="py-16 md:py-24 bg-[#0f1e32]" data-testid="product-specs">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="font-mono text-xs text-[#E63946] uppercase tracking-widest mb-4 block">
                Technical
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
                Specifications
              </h2>
            </div>

            <div className="industrial-card bg-[#0a1628] border border-white/10 overflow-hidden">
              {product.specs.map((spec, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between p-6 ${
                    index !== product.specs.length - 1 ? 'border-b border-white/5' : ''
                  }`}
                >
                  <span className="text-gray-400">{spec.label}</span>
                  <span className="font-mono text-[#E63946] font-medium">{spec.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="industrial-card gold-corners bg-[#0f1e32] border border-[#E63946]/30 p-12 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
              Contact our team to discuss your requirements and get a customized quote for {product.name}.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/get-quote" data-testid="product-detail-get-quote">
                <Button className="btn-gold clip-slant h-12 px-8">
                  Get a Quote <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/products">
                <Button className="btn-secondary h-12 px-8">
                  View All Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
